import i18n from '../i18n';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import ReactMarkdown from 'react-markdown';
import ReactDOMServer from 'react-dom/server';
import { apiFetch } from './apiClient';



// Function to save text as a .txt file
export const saveAsTextFile = (text, filename = 'note.txt') => {
  text = removeMarkdown(text).trim();
  const element = document.createElement('a');
  const file = new Blob([text], { type: 'text/plain' });
  element.href = URL.createObjectURL(file);
  element.download = filename;
  document.body.appendChild(element); // Required for this to work in FireFox
  element.click();
  document.body.removeChild(element);
};

export async function downloadAsPdf(markdownContent) {
  // Split the markdown content into logical sections based on headers and paragraphs
  const sections = splitMarkdownIntoSections(markdownContent);

  // Initialize jsPDF
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const marginX = 10; // 10mm margin on left/right
  const marginY = 10; // 10mm margin on top/bottom
  const contentWidth = pdfWidth - (marginX * 2);

  // Track current Y position on page
  let currentY = marginY;
  let isFirstPage = true;

  // Create custom stylesheet to control line spacing
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    .pdf-section p {
      margin-top: 0.5em;
      margin-bottom: 0.5em;
      line-height: 1.3;
    }
    .pdf-section h1, .pdf-section h2, .pdf-section h3,
    .pdf-section h4, .pdf-section h5, .pdf-section h6 {
      margin-top: 0.8em;
      margin-bottom: 0.3em;
      line-height: 1.2;
    }
    .pdf-section ul, .pdf-section ol {
      margin-top: 0.3em;
      margin-bottom: 0.3em;
    }
    .pdf-section li {
      margin-top: 0.1em;
      margin-bottom: 0.1em;
    }
  `;
  document.head.appendChild(styleSheet);

  // Process each section
  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];

    // Convert section Markdown to HTML
    const sectionHtmlContent = ReactDOMServer.renderToStaticMarkup(
      <ReactMarkdown>{section}</ReactMarkdown>
    );

    // Create a container for this section
    const container = document.createElement('div');
    container.innerHTML = sectionHtmlContent;
    container.dir = 'auto';
    container.style.textAlign = 'start';
    container.className = 'pdf-section'; // Apply the custom styles
    container.style.position = 'absolute';
    container.style.top = '-10000px';
    container.style.left = '-10000px';
    container.style.width = `${contentWidth}mm`;
    container.style.padding = '0';
    container.style.boxSizing = 'border-box';
    container.style.fontSize = '12pt';
    // System fonts can have different DOM/canvas metrics, causing word runs to overlap.
    container.style.fontFamily = 'Arial, sans-serif';
    document.body.appendChild(container);

    try {
      // Capture section as canvas
      const canvas = await html2canvas(container, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');

      // Calculate dimensions for this section
      const canvasWidthInMm = (canvas.width * 25.4) / 96;
      const canvasHeightInMm = (canvas.height * 25.4) / 96;
      const aspectRatio = canvasHeightInMm / canvasWidthInMm;
      const scaledWidth = contentWidth;
      const scaledHeight = scaledWidth * aspectRatio;

      // Check if this section needs a new page
      if (!isFirstPage) {
        const remainingSpace = pdfHeight - currentY - marginY;

        // If section won't fit, add a new page
        if (scaledHeight > remainingSpace) {
          pdf.addPage();
          currentY = marginY; // Reset Y position for new page
        }
      }

      // Add section to PDF
      pdf.addImage(
        imgData,
        'PNG',
        marginX,
        currentY,
        scaledWidth,
        scaledHeight
      );

      // Update the Y position for next section
      currentY += scaledHeight + 2; // Reduced spacing between sections to 2mm
      isFirstPage = false;
    } finally {
      document.body.removeChild(container);
    }
  }

  // Clean up the added stylesheet
  document.head.removeChild(styleSheet);

  pdf.save('note.pdf');
}

// Helper function to split markdown into logical sections
function splitMarkdownIntoSections(markdown) {
  // Split by headers (# Header, ## Subheader, etc.)
  const headerRegex = /^(#{1,6}\s+.+)$/gm;

  // Find all header positions
  const headers = [...markdown.matchAll(headerRegex)];
  const sections = [];

  if (headers.length === 0) {
    // If no headers, split by double newlines (paragraphs)
    return markdown.split(/\n\n+/).filter(section => section.trim() !== '');
  }

  // Process based on headers
  headers.forEach((header, index) => {
    const startPos = header.index;
    const endPos = index < headers.length - 1 ? headers[index + 1].index : markdown.length;

    // Extract section including its header
    const section = markdown.substring(startPos, endPos).trim();

    // Further split large sections by paragraphs if needed
    if (section.length > 1000) { // Arbitrary threshold
      const paragraphs = section.split(/\n\n+/);

      // Always keep the header with the first paragraph
      if (paragraphs.length > 1) {
        sections.push(paragraphs[0]);
        sections.push(...paragraphs.slice(1));
      } else {
        sections.push(section);
      }
    } else {
      sections.push(section);
    }
  });

  // Add content before the first header if it exists
  if (headers.length > 0 && headers[0].index > 0) {
    const preContent = markdown.substring(0, headers[0].index).trim();
    if (preContent) {
      sections.unshift(preContent);
    }
  }

  // If no sections were created, return the original as one section
  return sections.length > 0 ? sections : [markdown];
}

/**
 * Removes Markdown formatting from a string.
 * @param {string} markdownText The text with Markdown.
 * @returns {string} The text with Markdown formatting removed.
 */
const removeMarkdown = (markdownText) => {
  if (typeof markdownText !== 'string') {
    // Handle non-string inputs, e.g., return empty string or the input itself if null/undefined
    return markdownText === null || markdownText === undefined ? '' : String(markdownText);
  }

  let plainText = markdownText;

  // Replace bold text (keeping content)
  plainText = plainText.replace(/\*\*(.*?)\*\*/g, '$1');
  plainText = plainText.replace(/__(.*?)__/g, '$1');

  // Replace italicized text (keeping content)
  plainText = plainText.replace(/_(.*?)_/g, '$1'); // Handles _italic_
  plainText = plainText.replace(/\*(.*?)\*/g, '$1'); // Handles *italic*

  // Replace strikethrough text (keeping content)
  plainText = plainText.replace(/~~(.*?)~~/g, '$1');

  // Replace inline code blocks (keeping content)
  plainText = plainText.replace(/`(.+?)`/g, '$1');

  // Remove code blocks entirely (including content)
  plainText = plainText.replace(/```[\s\S]*?```/g, '');

  // Remove links, keeping the link text
  plainText = plainText.replace(/\[(.*?)\]\((.*?)\)/g, '$1');

  // Remove images, keeping the alt text
  plainText = plainText.replace(/!\[(.*?)\]\((.*?)\)/g, '$1');

  // Remove ATX-style headings (e.g., # Heading), keeping the heading text
  plainText = plainText.replace(/^#+\s+(.*?)\s*$/gm, '$1');

  // Remove Setext-style heading underlines (=== or --- on their own line)
  // This rule was previously too aggressive and removed the line above the '---'
  // The original Dart code only removed the underline itself.
  plainText = plainText.replace(/^\s*(=+|-+)\s*$/gm, '');

  // Remove blockquotes, keeping the content
  plainText = plainText.replace(/^\s*>\s+(.*?)\s*$/gm, '$1');

  plainText = plainText.replace(/^\s*([-*_])\1{2,}\s*$/gm, ''); // More specific for ***, ---, ___

  return plainText;
};


// Update the function signature to accept `buttonElement`
export const copyPlainText = (markdownText, buttonElement) => {
  const plainText = removeMarkdown(markdownText).trim();

  navigator.clipboard.writeText(plainText)
    .then(() => {
      // --- ADD THIS BACK: Logic for the button click animation ---
      if (buttonElement) {
        buttonElement.classList.add('clicked');
        setTimeout(() => {
          buttonElement.classList.remove('clicked');
        }, 200); // Match your animation duration
      }

      // --- Logic for the speech bubble (already here) ---
      const copyMessage = document.getElementById('copy-message');
      if (copyMessage) {
        copyMessage.classList.add('show');
        setTimeout(() => {
          copyMessage.classList.remove('show');
        }, 2000);
      }

      console.log('Plain text copied to clipboard');
    })
    .catch(err => {
      console.error('Failed to copy text: ', err);
    });
};

// Function to copy text to the clipboard
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);

  } catch (err) {
    console.error('Failed to copy text: ', err);
  }
};

export const generateDiagnosis = async (noteText) => {
  try {
    const response = await apiFetch('/generate-diagnosis', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        note: noteText
      }),
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(i18n.t('errors.diagnosis'));
    }

    const data = await response.json();
    alert(data.diagnosis);

  } catch (error) {
    alert(i18n.t('errors.diagnosis'));
  }
};

